// Cloud-backed drafts: syncs to /api/drafts so a draft roams across devices.
// Falls back to IndexedDB when offline so nothing is ever lost.
import { api } from "./api";

const DB_NAME = "sonotek_drafts";
const STORE = "kv";

function open() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}
async function tx(mode, fn) {
  const db = await open();
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const req = fn(t.objectStore(STORE));
    t.oncomplete = () => resolve(req?.result);
    t.onerror = () => reject(t.error);
  });
}
const idbGet = (k) => tx("readonly", (s) => s.get(k));
const idbSet = (k, v) => tx("readwrite", (s) => s.put(v, k));
const idbDel = (k) => tx("readwrite", (s) => s.delete(k));
const LOCAL_CACHE = "drafts_cache_v3";

export async function listDrafts() {
  try {
    const { data } = await api.get("/drafts");
    const norm = data.map((d) => ({
      id: d.id, name: d.name, saved_at: new Date(d.updated_at).getTime(),
      has_artwork: !!d.has_artwork, has_audio: !!d.has_audio, song_title: d.song_title,
    }));
    await idbSet(LOCAL_CACHE, norm);
    return norm;
  } catch {
    return (await idbGet(LOCAL_CACHE)) || [];
  }
}

async function fetchBlob(path) {
  try {
    const token = localStorage.getItem("sd_token");
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}${path}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return null;
    return await res.blob();
  } catch { return null; }
}

export async function loadDraft(id) {
  const { data } = await api.get(`/drafts/${id}`);
  const artwork = data.has_artwork ? await fetchBlob(`/api/drafts/${id}/artwork`) : null;
  const audio = data.has_audio ? await fetchBlob(`/api/drafts/${id}/audio`) : null;
  return { form: data.form || {}, artwork, audio, saved_at: new Date(data.updated_at).getTime(), name: data.name };
}

export async function upsertDraft({ id, name, form, artwork, audio }) {
  const fd = new FormData();
  if (id) fd.append("id", id);
  if (name) fd.append("name", name);
  fd.append("form_json", JSON.stringify(form || {}));
  if (artwork instanceof Blob) {
    const filename = artwork.name || "artwork.jpg";
    fd.append("artwork", artwork, filename);
  }
  if (audio instanceof Blob) {
    const filename = audio.name || "audio.mp3";
    fd.append("audio", audio, filename);
  }
  const { data } = await api.post("/drafts", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data.id;
}

export async function removeDraft(id) {
  try { await api.delete(`/drafts/${id}`); } catch {}
  const cache = (await idbGet(LOCAL_CACHE)) || [];
  await idbSet(LOCAL_CACHE, cache.filter((d) => d.id !== id));
}
